'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { FaCaretDown, FaCaretUp, FaChevronRight } from 'react-icons/fa6';
import type { Mover, Movers } from '@benzinga/movers-manager';
import { ErrorBoundary } from '@benzinga/core-ui';

import styles from './styles.module.scss';
import { MenuList, QuoteCard } from './QuoteCard';

type MoversTab = 'all' | 'gainers' | 'losers';

export interface MoversBoardProps {
  movers: Movers | null;
  title?: string;
  gainersLabel?: string;
  losersLabel?: string;
  viewMoreHref?: string;
  /** `tabs` = single grid with menu; `split` = side-by-side gainers/losers */
  layout?: 'tabs' | 'split';
  maxPerSection?: number;
  /** Optional controls (e.g. session tabs) rendered in the board header beside the title */
  controls?: ReactNode;
}

function toQuoteTicker(mover: Mover) {
  return {
    ...mover,
    lastTradePrice: mover.price ?? mover.close,
    name: mover.companyName,
  };
}

function MoversColumnHeader({
  icon,
  label,
  variant,
}: {
  icon: React.ReactNode;
  label: string;
  variant: 'gainers' | 'losers';
}) {
  return (
    <div
      className={`${styles['movers-column-header']} ${styles[`movers-column-header-${variant}`]}`}
    >
      <span className={styles['movers-column-icon']}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function MoversGrid({
  movers,
  emptyLabel,
  className = 'mt-3',
  gridClassName = 'grid-cols-1 sm:grid-cols-2',
}: {
  movers: Mover[];
  emptyLabel: string;
  className?: string;
  gridClassName?: string;
}) {
  if (movers.length === 0) {
    return <div className="p-4 text-gray-500 text-center min-h-32">{emptyLabel}</div>;
  }

  return (
    <div className={`grid gap-2 ${gridClassName} ${className}`}>
      {movers.map((mover, index) => (
        <QuoteCard key={`${mover.symbol}-${index}`} showLogo ticker={toQuoteTicker(mover)} variant="grid" />
      ))}
    </div>
  );
}

export function MoversBoard({
  movers,
  title = 'Stock Movers',
  gainersLabel = 'Gainers',
  losersLabel = 'Losers',
  viewMoreHref = '/movers',
  layout = 'tabs',
  maxPerSection = 12,
  controls,
}: MoversBoardProps) {
  'use memo';
  const [tab, setTab] = useState<MoversTab>('all');

  const gainers = useMemo(() => (movers?.gainers ?? []).slice(0, maxPerSection), [movers, maxPerSection]);
  const losers = useMemo(() => (movers?.losers ?? []).slice(0, maxPerSection), [movers, maxPerSection]);
  const all = useMemo(() => {
    if (movers?.all?.length) return movers.all.slice(0, maxPerSection);
    return [...gainers, ...losers]
      .sort((a, b) => Math.abs(b.changePercent) - Math.abs(a.changePercent))
      .slice(0, maxPerSection);
  }, [movers, gainers, losers, maxPerSection]);

  const tabItems = [
    { label: 'Top', value: 'all' as const },
    { label: gainersLabel, value: 'gainers' as const },
    { label: losersLabel, value: 'losers' as const },
  ];

  const activeMovers = tab === 'all' ? all : tab === 'gainers' ? gainers : losers;

  const tabControls =
    layout === 'tabs' ? (
      <MenuList compact currentItem={tab} items={tabItems} onClick={(value: MoversTab) => setTab(value)} />
    ) : null;

  const headerControls = controls ?? tabControls;

  const showTitle = Boolean(title);
  const headerClassName = [
    styles['block-header'],
    !showTitle && headerControls ? styles['block-header-controls-only'] : '',
    viewMoreHref ? styles['block-header-with-view-more'] : '',
  ]
    .filter(Boolean)
    .join(' ');

  const viewMoreLink = viewMoreHref ? (
    <a className={`${styles['block-header-actions']} ${styles['block-header-actions-desktop']}`} href={viewMoreHref}>
      View More <FaChevronRight size={12} />
    </a>
  ) : null;

  const viewMoreFooter = viewMoreHref ? (
    <a className={styles['block-footer-action']} href={viewMoreHref}>
      View More <FaChevronRight size={12} />
    </a>
  ) : null;

  return (
    <ErrorBoundary name="movers-board">
      <div className={styles['block-container']}>
        {(showTitle || headerControls || viewMoreHref) && (
          <div className={headerClassName}>
            {showTitle ? <h2>{title}</h2> : null}
            {(headerControls || viewMoreLink) && (
              <div className={styles['block-header-end']}>
                {headerControls}
                {viewMoreLink}
              </div>
            )}
          </div>
        )}
        <div className={styles['block-content']}>
          {layout === 'split' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-4 lg:gap-y-0">
              <div>
                <MoversColumnHeader
                  icon={<FaCaretUp size={12} />}
                  label={gainersLabel}
                  variant="gainers"
                />
                <MoversGrid
                  emptyLabel="No gainers available"
                  gridClassName="grid-cols-1 sm:grid-cols-2"
                  movers={gainers}
                />
              </div>
              <div>
                <MoversColumnHeader
                  icon={<FaCaretDown size={12} />}
                  label={losersLabel}
                  variant="losers"
                />
                <MoversGrid
                  emptyLabel="No losers available"
                  gridClassName="grid-cols-1 sm:grid-cols-2"
                  movers={losers}
                />
              </div>
            </div>
          ) : (
            <MoversGrid
              emptyLabel="No movers available"
              gridClassName="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              movers={activeMovers}
            />
          )}
        </div>
        {viewMoreFooter ? <div className={styles['block-footer']}>{viewMoreFooter}</div> : null}
      </div>
    </ErrorBoundary>
  );
}
